import { ResponsiveTable } from '../components/ResponsiveTable';
import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Search,
  Filter,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Edit,
  Eye,
  Plus,
  Mail,
  Star,
  AlertCircle,
  Palette,
  Loader2,
  UserPlus,
  FileText,
  X,
  Calendar,
  Trash,
} from 'lucide-react';
import { ConfirmDefinitiveDeleteModal } from '../components/ConfirmDefinitiveDeleteModal';
import { HelpIcon } from '../components/HelpIcon';
import { apiClient } from '../utils/api';
import { useShopScopedPage } from '../hooks/useShopScopedReload';
import { ShopSelectionGate } from '../components/ShopSelectionGate';
import { ShopColumnHeader, ShopColumnCell } from '../components/ShopListColumn';
import { TableScrollContainer } from '../components/TableScrollContainer';
import { useVisibleSelection } from '../hooks/useVisibleSelection';
import { useToast } from '../components/Toast';
import { RootState } from '../store';

interface Practitioner {
  id: string;
  firstName: string;
  lastName: string;
  artistName?: string | null;
  email: string;
  phone: string;
  userEmail?: string | null;
  specialties: Array<{
    category: string;
    styles: string[];
    experience: number;
  }>;
  bio: string;
  experience: number;
  rating: number;
  reviewCount: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  shopName?: string | null;
}

interface ShopOption {
  id: string;
  name: string;
  address?: {
    city?: string;
  };
}

interface PractitionersResponse {
  success: boolean;
  data: {
    practitioners: Practitioner[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  message?: string;
}

export function PractitionersListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showToast, ToastComponent } = useToast();
  const { user } = useSelector((state: RootState) => state.auth);
  const canHardDelete = user?.role === 'super_admin';
  
  const [practitioners, setPractitioners] = useState<Practitioner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Documents modal state
  const [docsOpen, setDocsOpen] = useState(false);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docsError, setDocsError] = useState<string | null>(null);
  const [docs, setDocs] = useState<
    Array<{
      id: string;
      templateCode: string;
      templateVersion: number;
      entityType: string;
      entityId: string;
      clientFullName?: string;
      serviceName?: string;
      pdfPath?: string | null;
      docxPath?: string | null;
      createdAt: string;
      isSigned?: boolean;
      hasSignedPdf?: boolean;
    }>
  >([]);
  const [docsTotal, setDocsTotal] = useState(0);
  const [docsPage, setDocsPage] = useState(1);
  const [docsPageSize] = useState(50);
  const [docsMonthOffset, setDocsMonthOffset] = useState(0); // 0 = current month, -1 = previous
  const [docsPractitioner, setDocsPractitioner] = useState<Practitioner | null>(null);
  const [selectedDocIds, setSelectedDocIds] = useState<string[]>([]);
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; deleting: boolean }>({
    isOpen: false,
    deleting: false,
  });
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState<{ isOpen: boolean; practitionerIds: string[] }>({
    isOpen: false,
    practitionerIds: [],
  });
  const [bulkHardDeleteConfirm, setBulkHardDeleteConfirm] = useState<{ isOpen: boolean; practitionerIds: string[] }>({
    isOpen: false,
    practitionerIds: [],
  });
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkHardDeleting, setBulkHardDeleting] = useState(false);
  const isBusy = loading || bulkDeleting || bulkHardDeleting || deleteConfirm.deleting || docsLoading;
  
  // Filters and pagination
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [specialtyFilter, setSpecialtyFilter] = useState('');
  const [showWithoutShopOnly, setShowWithoutShopOnly] = useState(false);
  const [availableShops, setAvailableShops] = useState<ShopOption[]>([]);
  const [assignShopModalOpen, setAssignShopModalOpen] = useState(false);
  const [assigningPractitioner, setAssigningPractitioner] = useState<Practitioner | null>(null);
  const [selectedAssignShopId, setSelectedAssignShopId] = useState('');
  const [assigningShop, setAssigningShop] = useState(false);
  const [assignShopError, setAssignShopError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  const limit = 20;

  const visiblePractitionerIds = useMemo(() => practitioners.map((p) => p.id), [practitioners]);
  const {
    selectedIds,
    selectedCount,
    selectedArray,
    clearSelection,
    selectAllCheckboxRef,
    allVisibleSelected,
    toggleSelect,
    toggleSelectAllVisible,
  } = useVisibleSelection<string>(visiblePractitionerIds);

  const normalizeSpecialties = (value: any): Practitioner['specialties'] => {
    let raw = value;

    // Some legacy rows can have JSON stored as a string
    if (typeof raw === 'string') {
      try {
        raw = JSON.parse(raw);
      } catch {
        raw = [];
      }
    }

    if (!Array.isArray(raw)) {
      return [];
    }

    return raw.map((specialty: any) => ({
      category: typeof specialty?.category === 'string' ? specialty.category : 'TATTOO',
      styles: Array.isArray(specialty?.styles) ? specialty.styles.filter((s: any) => typeof s === 'string') : [],
      experience:
        typeof specialty?.experience === 'number'
          ? specialty.experience
          : Number.isFinite(Number(specialty?.experience))
            ? Number(specialty?.experience)
            : 0,
    }));
  };

  const normalizePractitioner = (p: any): Practitioner => ({
    ...p,
    specialties: normalizeSpecialties(p?.specialties),
  });

  const { needsShopSelection, canLoad, currentShopId, isAllShopsMode } = useShopScopedPage();

  useEffect(() => {
    if (!canLoad) return;
    fetchPractitioners();
  }, [canLoad, currentShopId, showWithoutShopOnly, search, statusFilter, specialtyFilter, sortKey, sortDir, page]);

  const fetchPractitioners = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sortKey,
        sortDir,
        ...(showWithoutShopOnly && { withoutShop: 'true' }),
        ...(search && { search }),
        ...(statusFilter && { status: statusFilter }),
        ...(specialtyFilter && { specialty: specialtyFilter })
      });

      const response = await apiClient(`/api/admin/practitioners?${params.toString()}`);

      if (!response.ok) {
        throw new Error('Failed to fetch practitioners');
      }

      const data: PractitionersResponse = await response.json();
      
      if (data.success) {
        const normalized = (data.data?.practitioners || []).map(normalizePractitioner);
        setPractitioners(normalized);
        setTotal(data.data.total);
        setTotalPages(data.data.totalPages);
      } else {
        setError(data.message || 'Failed to load practitioners');
      }
    } catch (error) {
      console.error('Fetch practitioners error:', error);
      setError('Failed to load practitioners');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const handleEdit = (practitionerId: string) => {
    navigate(`/practitioners/${practitionerId}/edit`);
  };

  const fetchAvailableShops = async () => {
    try {
      const response = await apiClient('/api/admin/shops?limit=1000&allShops=true');
      if (!response.ok) {
        throw new Error('Failed to fetch shops');
      }
      const data = await response.json();
      if (data?.success) {
        setAvailableShops(Array.isArray(data.data?.shops) ? data.data.shops : []);
      }
    } catch (err) {
      console.error('Fetch shops for orphan assignment error:', err);
      setAssignShopError('Impossible de charger la liste des shops');
    }
  };

  const openAssignShopModal = async (practitioner: Practitioner) => {
    setAssigningPractitioner(practitioner);
    setSelectedAssignShopId('');
    setAssignShopError(null);
    setAssignShopModalOpen(true);
    if (availableShops.length === 0) {
      await fetchAvailableShops();
    }
  };

  const closeAssignShopModal = () => {
    if (assigningShop) return;
    setAssignShopModalOpen(false);
    setAssigningPractitioner(null);
    setSelectedAssignShopId('');
    setAssignShopError(null);
  };

  const handleAssignShop = async () => {
    if (!assigningPractitioner || !selectedAssignShopId) return;
    try {
      setAssigningShop(true);
      setAssignShopError(null);

      const response = await apiClient(
        `/api/planning/practitioners/${assigningPractitioner.id}/shops/${selectedAssignShopId}`,
        {
          method: 'POST',
          body: JSON.stringify({ relationshipType: 'FREELANCE' }),
        },
      );

      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || 'Impossible d\'associer le praticien au shop');
      }

      showToast('Shop attribué au praticien', 'success');
      closeAssignShopModal();
      await fetchPractitioners();
    } catch (err: any) {
      setAssignShopError(err?.message || 'Impossible d\'associer le praticien au shop');
    } finally {
      setAssigningShop(false);
    }
  };

  const handleView = (practitionerId: string) => {
    navigate(`/practitioners/${practitionerId}`);
  };

  const handleCreate = () => {
    navigate('/practitioners/create');
  };

  const getStatusBadge = (isActive: boolean) => {
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        isActive 
          ? 'bg-green-100 text-green-800' 
          : 'bg-gray-100 text-gray-800'
      }`}>
        {isActive ? 'Actif' : 'Inactif'}
      </span>
    );
  };

  // Removed unused formatDate function

  const renderRating = (rating: number, reviewCount: number) => {
    return (
      <div className="flex items-center space-x-1">
        <Star className="w-4 h-4 text-yellow-400 fill-current" />
        <span className="text-sm font-medium">{rating.toFixed(1)}</span>
        <span className="text-xs text-gray-500">({reviewCount})</span>
      </div>
    );
  };

  const getMonthRange = (offset: number) => {
    const now = new Date();
    const from = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + offset, 1, 0, 0, 0));
    const to = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + offset + 1, 1, 0, 0, 0));
    return { from: from.toISOString(), to: to.toISOString() };
  };

  const fetchDocs = async (practitioner: Practitioner, page: number, monthOffset: number) => {
    try {
      setDocsLoading(true);
      setDocsError(null);
      const token = localStorage.getItem('token');
      const { from, to } = getMonthRange(monthOffset);
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: docsPageSize.toString(),
        entityTypes: 'CONTRACT,APPOINTMENT,INVOICE',
        from,
        to,
      });
      const resp = await fetch(`/api/entities/PRACTITIONER/${practitioner.id}/documents?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!resp.ok) {
        throw new Error('Impossible de charger les documents');
      }
      const data = await resp.json();
      setDocs(data.data || []);
      setDocsTotal(data.total || 0);
      setDocsPage(page);
      setDocsMonthOffset(monthOffset);
      setSelectedDocIds([]);
    } catch (err: any) {
      setDocsError(err?.message || 'Erreur lors du chargement des documents');
    } finally {
      setDocsLoading(false);
    }
  };

  const openDocsModal = (practitioner: Practitioner) => {
    setDocsPractitioner(practitioner);
    setDocsOpen(true);
    setDocsPage(1);
    setDocsMonthOffset(0);
    setSelectedDocIds([]);
    fetchDocs(practitioner, 1, 0);
  };

  const closeDocsModal = () => {
    setDocsOpen(false);
    setDocsPractitioner(null);
    setDocs([]);
    setDocsError(null);
  };

  const toggleDocSelection = (docId: string, isSigned?: boolean) => {
    if (isSigned) return;
    setSelectedDocIds((current) =>
      current.includes(docId) ? current.filter((id) => id !== docId) : [...current, docId],
    );
  };

  const toggleSelectAllDocs = () => {
    const selectableIds = docs.filter((d) => !d.isSigned).map((d) => d.id);
    const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedDocIds.includes(id));
    setSelectedDocIds(allSelected ? [] : selectableIds);
  };

  const handleConfirmDelete = () => {
    if (!selectedDocIds.length) return;
    setDeleteConfirm({ isOpen: true, deleting: false });
  };

  const handleBulkDelete = async () => {
    if (!docsPractitioner || !selectedDocIds.length) return;
    try {
      setDeleteConfirm((state) => ({ ...state, deleting: true }));
      setDocsError(null);
      const token = localStorage.getItem('token');
      const resp = await fetch('/api/documents', {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ids: selectedDocIds }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data?.message || 'Suppression des documents impossible');
      }
      if (data?.data?.blocked?.length) {
        setDocsError('Certains documents n’ont pas pu être supprimés (signés ou introuvables).');
      }
      setSelectedDocIds([]);
      await fetchDocs(docsPractitioner, docsPage, docsMonthOffset);
    } catch (err: any) {
      setDocsError(err?.message || 'Erreur lors de la suppression des documents');
    } finally {
      setDeleteConfirm({ isOpen: false, deleting: false });
    }
  };

  const closeDeleteConfirm = () => setDeleteConfirm({ isOpen: false, deleting: false });

  const openBulkDelete = () => {
    if (!selectedArray.length) return;
    setBulkDeleteConfirm({ isOpen: true, practitionerIds: selectedArray });
  };

  const openRowDelete = (practitionerId: string) => {
    setBulkDeleteConfirm({ isOpen: true, practitionerIds: [practitionerId] });
  };

  const cancelBulkDelete = () => {
    setBulkDeleteConfirm({ isOpen: false, practitionerIds: [] });
  };

  const confirmBulkDelete = async () => {
    if (!bulkDeleteConfirm.practitionerIds.length) return;
    try {
      setBulkDeleting(true);
      const failed: string[] = [];

      for (const practitionerId of bulkDeleteConfirm.practitionerIds) {
        const response = await apiClient(`/api/admin/practitioners/${practitionerId}`, {
          method: 'DELETE',
        });
        const data = await response.json().catch(() => null);
        if (!response.ok || !data?.success) {
          failed.push(practitionerId);
        }
      }

      const deletedCount = bulkDeleteConfirm.practitionerIds.length - failed.length;
      if (deletedCount > 0) {
        showToast(`${deletedCount} praticien(s) supprimé(s)`, 'success');
      }
      if (failed.length > 0) {
        showToast(`Échec de suppression pour ${failed.length} praticien(s).`, 'error', 7000);
      }

      await fetchPractitioners();
      clearSelection();
    } catch (err) {
      console.error('Bulk delete practitioner error:', err);
      showToast('Impossible de supprimer les praticiens', 'error', 7000);
    } finally {
      setBulkDeleting(false);
      cancelBulkDelete();
    }
  };

  const openBulkHardDelete = () => {
    if (!canHardDelete || !selectedArray.length) return;
    setBulkHardDeleteConfirm({ isOpen: true, practitionerIds: selectedArray });
  };

  const cancelBulkHardDelete = () => {
    setBulkHardDeleteConfirm({ isOpen: false, practitionerIds: [] });
  };

  const confirmBulkHardDelete = async () => {
    if (!bulkHardDeleteConfirm.practitionerIds.length) return;
    try {
      setBulkHardDeleting(true);
      const failed: string[] = [];

      for (const practitionerId of bulkHardDeleteConfirm.practitionerIds) {
        const response = await apiClient(`/api/admin/practitioners/${practitionerId}?hardDelete=true`, {
          method: 'DELETE',
        });
        const data = await response.json().catch(() => null);
        if (!response.ok || !data?.success) {
          failed.push(practitionerId);
        }
      }

      const deletedCount = bulkHardDeleteConfirm.practitionerIds.length - failed.length;
      if (deletedCount > 0) {
        showToast(`${deletedCount} praticien(s) supprimé(s) définitivement`, 'success');
      }
      if (failed.length > 0) {
        showToast(`Échec de suppression définitive pour ${failed.length} praticien(s).`, 'error', 7000);
      }

      await fetchPractitioners();
      clearSelection();
    } catch (err) {
      console.error('Bulk hard delete practitioner error:', err);
      showToast('Impossible de supprimer définitivement les praticiens', 'error', 7000);
    } finally {
      setBulkHardDeleting(false);
      cancelBulkHardDelete();
    }
  };

  const openDocumentFile = async (
    docId: string,
    kind: 'pdf' | 'docx',
    variant?: 'original' | 'signed',
  ) => {
    try {
      setDocsError(null);
      const token = localStorage.getItem('token');
      const query = variant ? `?variant=${variant}` : '';
      const resp = await fetch(`/api/documents/${docId}/${kind}${query}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!resp.ok) {
        throw new Error('Impossible de récupérer le document');
      }
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (err: any) {
      setDocsError(err?.message || 'Erreur lors de l’ouverture du document');
    }
  };

  const monthLabel = (offset: number) => {
    const now = new Date();
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + offset, 1));
    return d.toLocaleString('fr-FR', { month: 'long', year: 'numeric' });
  };

  if (needsShopSelection) {
    return <ShopSelectionGate />;
  }

  if (loading && practitioners.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex items-center space-x-2">
          <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          <span className="text-gray-600">Chargement des artistes...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`p-6 ${isBusy ? 'cursor-wait' : ''}`}>
      {/* Header: same layout as Shops – title left, Add + bulk actions right */}
      <div className="mb-8">
        <div className="dt-list-toolbar flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div>
              <h1 className="text-2xl font-display font-bold text-textPrimary flex items-center space-x-2">
                <span>{t('navigation.practitioners.management')}</span>
                <HelpIcon 
                  docPath="06-gestion-praticiens#06-2-liste" 
                  tooltip="Documentation : Liste des praticiens"
                />
              </h1>
              <p className="text-textSecondary mt-1">
                {t('navigation.practitioners.totalCount', { count: total })}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {showWithoutShopOnly ? (
              <button
                onClick={() => {
                  setShowWithoutShopOnly(false);
                  setPage(1);
                }}
                className="flex items-center space-x-2 px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <X className="w-4 h-4" />
                <span>Retour a la liste</span>
              </button>
            ) : (
              <button
                onClick={() => {
                  setShowWithoutShopOnly(true);
                  setPage(1);
                }}
                className="flex items-center space-x-2 px-4 py-2 rounded-lg border bg-white text-gray-700 border-gray-300 hover:bg-gray-50 transition-colors"
              >
                <AlertCircle className="w-4 h-4" />
                <span>Praticiens sans shop</span>
              </button>
            )}
            <button
              onClick={handleCreate}
              className="btn-primary flex items-center space-x-2"
            >
              <UserPlus className="w-4 h-4" />
              <span>{t('navigation.practitioners.create')}</span>
            </button>
          </div>
          {selectedCount > 0 && (
            <div className="ml-3 flex items-center space-x-2">
              <button
                onClick={openBulkDelete}
                disabled={bulkDeleting || bulkHardDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 inline-flex items-center space-x-2"
                title="Supprimer les praticiens sélectionnés"
              >
                <Trash className="w-4 h-4" />
                <span>Supprimer ({selectedCount})</span>
              </button>
              {canHardDelete && (
                <button
                  onClick={openBulkHardDelete}
                  disabled={bulkDeleting || bulkHardDeleting}
                  className="px-4 py-2 bg-red-800 text-white rounded-lg hover:bg-red-900 disabled:opacity-50 inline-flex items-center space-x-2"
                  title="Supprimer définitivement les praticiens sélectionnés"
                >
                  <AlertCircle className="w-4 h-4" />
                  <span>Suppression définitive ({selectedCount})</span>
                </button>
              )}
            </div>
          )}
        </div>
        {showWithoutShopOnly && (
          <p className="mt-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 inline-flex items-center">
            Vue orphelins: utilisez "Attribuer un shop" avant de modifier un praticien.
          </p>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="dt-filters grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Rechercher un artiste..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input-field pl-10"
            />
          </div>

          {/* Status Filter */}
          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input-field appearance-none pr-10"
            >
              <option value="">Tous les statuts</option>
              <option value="active">Actif</option>
              <option value="inactive">Inactif</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
          </div>

          {/* Specialty Filter */}
          <div className="relative">
            <select
              value={specialtyFilter}
              onChange={(e) => setSpecialtyFilter(e.target.value)}
              className="input-field appearance-none pr-10"
            >
              <option value="">Toutes les spécialités</option>
              <option value="TATTOO">Tatouage</option>
              <option value="PIERCING">Piercing</option>
              <option value="BOTH">Les deux</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
          </div>

          {/* Clear Filters */}
          <button
            onClick={() => {
              setSearch('');
              setStatusFilter('');
              setSpecialtyFilter('');
              setPage(1);
            }}
            className="btn-secondary flex items-center justify-center space-x-2"
          >
            <Filter className="w-4 h-4" />
            <span>Effacer</span>
          </button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-red-800 font-medium">{error}</span>
          </div>
          <button
            onClick={fetchPractitioners}
            className="mt-2 text-sm text-red-600 hover:text-red-700 underline"
          >
            Réessayer
          </button>
        </div>
      )}

      {/* Practitioners Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <TableScrollContainer hint="Faites défiler →">
          <ResponsiveTable className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    ref={selectAllCheckboxRef}
                    type="checkbox"
                    checked={allVisibleSelected}
                    disabled={bulkDeleting || bulkHardDeleting}
                    onChange={toggleSelectAllVisible}
                    className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                  />
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('firstName')}
                >
                  <div className="flex items-center space-x-1">
                    <Palette className="w-4 h-4" />
                    <span>{t('navigation.practitioners.title')}</span>
                    {sortKey === 'firstName' && (
                      <ChevronDown className={`w-4 h-4 transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('email')}
                >
                  <div className="flex items-center space-x-1">
                    <Mail className="w-4 h-4" />
                    <span>Contact</span>
                    {sortKey === 'email' && (
                      <ChevronDown className={`w-4 h-4 transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('userEmail')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Connexion</span>
                    {sortKey === 'userEmail' && (
                      <ChevronDown className={`w-4 h-4 transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('specialty')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Spécialités</span>
                    {sortKey === 'specialty' && (
                      <ChevronDown className={`w-4 h-4 transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('experience')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Expérience</span>
                    {sortKey === 'experience' && (
                      <ChevronDown className={`w-4 h-4 transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('rating')}
                >
                  <div className="flex items-center space-x-1">
                    <Star className="w-4 h-4" />
                    <span>Note</span>
                    {sortKey === 'rating' && (
                      <ChevronDown className={`w-4 h-4 transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <ShopColumnHeader visible={isAllShopsMode} />
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('isActive')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Statut</span>
                    {sortKey === 'isActive' && (
                      <ChevronDown className={`w-4 h-4 transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Docs
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50 z-10 border-l border-gray-200">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {practitioners.map((practitioner) => (
                <tr key={practitioner.id} className="group hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(practitioner.id)}
                      disabled={bulkDeleting || bulkHardDeleting}
                      onChange={() => toggleSelect(practitioner.id)}
                      className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {practitioner.firstName} {practitioner.lastName}
                        {practitioner.artistName && (
                          <span className="ml-2 text-xs font-normal text-indigo-600">({practitioner.artistName})</span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500 truncate max-w-xs">
                        {practitioner.bio}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{practitioner.email}</div>
                    <div className="text-sm text-gray-500">{practitioner.phone}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {practitioner.userEmail ? (
                      <span className="text-sm text-gray-900" title={t('practitionerAccount.loginAccount')}>{practitioner.userEmail}</span>
                    ) : (
                      <span className="text-sm text-amber-600 italic" title={t('practitionerAccount.noAccountTooltip')}>{t('practitionerAccount.noAccount')}</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-wrap gap-1">
                      {(Array.isArray(practitioner.specialties) ? practitioner.specialties : []).slice(0, 2).map((specialty, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {specialty?.category || '—'}
                        </span>
                      ))}
                      {(Array.isArray(practitioner.specialties) ? practitioner.specialties : []).length > 2 && (
                        <span className="text-xs text-gray-500">
                          +{(Array.isArray(practitioner.specialties) ? practitioner.specialties : []).length - 2}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {practitioner.experience} ans
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {renderRating(practitioner.rating, practitioner.reviewCount)}
                  </td>
                  <ShopColumnCell visible={isAllShopsMode} shopName={practitioner.shopName} />
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(practitioner.isActive)}
                  </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => openDocsModal(practitioner)}
                    className="text-indigo-600 hover:text-indigo-800 p-1 rounded flex items-center space-x-1"
                    title="Voir les documents du mois en cours"
                  >
                    <FileText className="w-4 h-4" />
                    <span className="text-xs">Docs</span>
                  </button>
                </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium sticky right-0 bg-white group-hover:bg-gray-50 z-10 border-l border-gray-200">
                    <div className="flex items-center justify-end space-x-2">
                      {showWithoutShopOnly ? (
                        <button
                          onClick={() => openAssignShopModal(practitioner)}
                          className="px-2 py-1 text-xs font-medium rounded border border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-100"
                          title="Attribuer un shop"
                        >
                          Attribuer un shop
                        </button>
                      ) : (
                        <>
                          <button
                            onClick={() => handleView(practitioner.id)}
                            className="text-gray-400 hover:text-gray-600 p-1 rounded"
                            title="Voir l'artiste"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEdit(practitioner.id)}
                            className="text-primary-600 hover:text-primary-700 p-1 rounded"
                            title="Modifier l'artiste"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => openRowDelete(practitioner.id)}
                        className="text-red-600 hover:text-red-700 p-1 rounded"
                        title="Supprimer l'artiste"
                      >
                        <Trash className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </ResponsiveTable>
        </TableScrollContainer>

        {/* Empty State */}
        {!loading && practitioners.length === 0 && (
          <div className="text-center py-12">
            <Palette className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun artiste trouvé</h3>
            <p className="mt-1 text-sm text-gray-500">
              {search || statusFilter || specialtyFilter 
                ? 'Essayez de modifier vos filtres de recherche.'
                : 'Commencez par ajouter votre premier artiste.'
              }
            </p>
            <div className="mt-6">
              <button
                onClick={handleCreate}
                className="btn-primary flex items-center space-x-2 mx-auto"
              >
                <Plus className="w-4 h-4" />
                <span>{t('navigation.practitioners.create')}</span>
              </button>
            </div>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="dt-list-toolbar bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Précédent
              </button>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Suivant
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Affichage de <span className="font-medium">{(page - 1) * limit + 1}</span> à{' '}
                  <span className="font-medium">{Math.min(page * limit, total)}</span> sur{' '}
                  <span className="font-medium">{total}</span> résultats
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  
                  {/* Page numbers */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNum = Math.max(1, Math.min(totalPages - 4, page - 2)) + i;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          page === pageNum
                            ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Loading overlay for subsequent loads */}
      {loading && practitioners.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-50 cursor-wait">
          <div className="bg-white rounded-lg p-4 flex items-center space-x-2">
            <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
            <span>Chargement...</span>
          </div>
        </div>
      )}

      {assignShopModalOpen && assigningPractitioner && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Attribuer un shop</h3>
            <p className="text-sm text-gray-600 mb-4">
              {assigningPractitioner.firstName} {assigningPractitioner.lastName}
            </p>

            {assignShopError && (
              <div className="mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {assignShopError}
              </div>
            )}

            <label className="block text-sm font-medium text-gray-700 mb-2">Shop</label>
            <select
              value={selectedAssignShopId}
              onChange={(e) => setSelectedAssignShopId(e.target.value)}
              className="input-field mb-5"
              disabled={assigningShop}
            >
              <option value="">Sélectionner un shop</option>
              {availableShops.map((shop) => (
                <option key={shop.id} value={shop.id}>
                  {shop.name}{shop.address?.city ? ` - ${shop.address.city}` : ''}
                </option>
              ))}
            </select>

            <div className="flex justify-end space-x-3">
              <button
                onClick={closeAssignShopModal}
                className="btn-secondary"
                disabled={assigningShop}
              >
                Annuler
              </button>
              <button
                onClick={handleAssignShop}
                disabled={!selectedAssignShopId || assigningShop}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {assigningShop ? 'Attribution...' : 'Attribuer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Documents modal */}
      {docsOpen && docsPractitioner && (
        <>
          <div className={`fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4 ${docsLoading ? 'cursor-wait' : ''}`}>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
              <div className="px-4 py-3 border-b flex items-center justify-between">
                <div className="flex flex-col space-y-1">
                  <div className="flex items-center space-x-2">
                    <FileText className="w-5 h-5 text-indigo-600" />
                    <h3 className="text-lg font-semibold">Documents – {docsPractitioner.firstName} {docsPractitioner.lastName}</h3>
                  </div>
                  <p className="text-sm text-gray-500">Mois : {monthLabel(docsMonthOffset)} (par défaut 1 mois max)</p>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => {
                      const newOffset = docsMonthOffset - 1;
                      fetchDocs(docsPractitioner, 1, newOffset);
                    }}
                    className="btn-secondary flex items-center space-x-1"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    <span>Mois précédent</span>
                  </button>
                  <button
                    onClick={() => {
                      if (docsMonthOffset >= 0) return;
                      const newOffset = docsMonthOffset + 1;
                      fetchDocs(docsPractitioner, 1, newOffset);
                    }}
                    className={`btn-secondary flex items-center space-x-1 ${docsMonthOffset >= 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                    disabled={docsMonthOffset >= 0}
                  >
                    <span>Mois suivant</span>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                  <button
                    onClick={handleConfirmDelete}
                    disabled={selectedDocIds.length === 0}
                    className="btn-danger flex items-center space-x-1 disabled:opacity-50"
                  >
                    <span>Supprimer la sélection</span>
                  </button>
                  <button
                    onClick={() => fetchDocs(docsPractitioner, 1, 0)}
                    className="btn-primary flex items-center space-x-1"
                  >
                    <Calendar className="w-4 h-4" />
                    <span>Mois courant</span>
                  </button>
                  <button
                    onClick={closeDocsModal}
                    className="text-gray-400 hover:text-gray-600 p-1 rounded"
                    aria-label="Fermer"
                    title="Fermer"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto">
                {docsError && (
                  <div className="bg-red-50 border border-red-200 m-4 rounded-lg p-3 text-sm text-red-700">
                    {docsError}
                  </div>
                )}

                {docsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
                    <span className="ml-2 text-gray-600">Chargement des documents...</span>
                  </div>
                ) : docs.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">Aucun document pour cette période.</div>
                ) : (
                  <ResponsiveTable className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2">
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                            onChange={toggleSelectAllDocs}
                            checked={
                              docs.filter((d) => !d.isSigned).length > 0 &&
                              docs.filter((d) => !d.isSigned).every((d) => selectedDocIds.includes(d.id))
                            }
                            aria-label="Sélectionner tous les documents non signés"
                          />
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {docs.map((doc) => (
                        <tr key={doc.id}>
                          <td className="px-4 py-2">
                            <input
                              type="checkbox"
                              className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                              disabled={doc.isSigned}
                              checked={selectedDocIds.includes(doc.id)}
                              onChange={() => toggleDocSelection(doc.id, doc.isSigned)}
                              aria-label={`Sélectionner le document ${doc.id}`}
                            />
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700">
                            {new Date(doc.createdAt).toLocaleString('fr-FR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                            })}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700">{doc.entityType}</td>
                          <td className="px-4 py-2 text-sm">
                            {doc.isSigned ? (
                              <span className="inline-flex items-center px-2 py-1 text-xs font-semibold text-green-700 bg-green-100 rounded-full">
                                Signé
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-1 text-xs font-semibold text-gray-700 bg-gray-100 rounded-full">
                                Non signé
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700">{doc.clientFullName || '—'}</td>
                          <td className="px-4 py-2 text-sm text-gray-700">{doc.serviceName || '—'}</td>
                          <td className="px-4 py-2 text-sm text-gray-700">
                            <div className="flex items-center space-x-2">
                              {doc.pdfPath && (
                                <button
                                  onClick={() => openDocumentFile(doc.id, 'pdf')}
                                  className="text-indigo-600 hover:text-indigo-800 underline"
                                >
                                  {doc.hasSignedPdf ? 'PDF signé' : 'PDF'}
                                </button>
                              )}
                              {doc.hasSignedPdf && doc.pdfPath && (
                                <button
                                  onClick={() => openDocumentFile(doc.id, 'pdf', 'original')}
                                  className="text-gray-500 hover:text-gray-700 underline"
                                  title="Version originale non signée"
                                >
                                  Original
                                </button>
                              )}
                              {doc.docxPath && (
                                <button
                                  onClick={() => openDocumentFile(doc.id, 'docx')}
                                  className="text-gray-600 hover:text-gray-800 underline"
                                >
                                  DOCX
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </ResponsiveTable>
                )}
              </div>

              <div className="dt-list-toolbar px-4 py-3 border-t bg-gray-50 text-sm text-gray-600 flex items-center justify-between">
                <span>
                  Total : {docsTotal} document{docsTotal > 1 ? 's' : ''} – page {docsPage}
                </span>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => {
                      if (docsPage <= 1) return;
                      fetchDocs(docsPractitioner, docsPage - 1, docsMonthOffset);
                    }}
                    disabled={docsPage <= 1}
                    className="btn-secondary px-3 py-1 disabled:opacity-50"
                  >
                    Précédent
                  </button>
                  <button
                    onClick={() => fetchDocs(docsPractitioner, docsPage + 1, docsMonthOffset)}
                    className="btn-secondary px-3 py-1"
                    disabled={docs.length < docsPageSize}
                  >
                    Suivant
                  </button>
                  <button
                    onClick={closeDocsModal}
                    className="btn-primary px-3 py-1"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          </div>

          {deleteConfirm.isOpen && (
            <div className={`fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4 ${deleteConfirm.deleting ? 'cursor-wait' : ''}`}>
              <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
                <div className="px-4 py-3 border-b">
                  <h3 className="text-lg font-semibold">Confirmer la suppression</h3>
                </div>
                <div className="p-4 space-y-3 text-sm text-gray-700">
                  <p>
                    Vous êtes sur le point de supprimer {selectedDocIds.length} document
                    {selectedDocIds.length > 1 ? 's' : ''}. Les documents signés ne peuvent pas être supprimés.
                  </p>
                  {docsError && (
                    <div className="bg-red-50 border border-red-200 rounded px-3 py-2 text-red-700 text-sm">
                      {docsError}
                    </div>
                  )}
                </div>
                <div className="px-4 py-3 border-t bg-gray-50 flex items-center justify-end space-x-2">
                  <button onClick={closeDeleteConfirm} className="btn-secondary px-3 py-1" disabled={deleteConfirm.deleting}>
                    Annuler
                  </button>
                  <button
                    onClick={handleBulkDelete}
                    className="btn-danger px-3 py-1"
                    disabled={deleteConfirm.deleting}
                  >
                    {deleteConfirm.deleting ? 'Suppression...' : 'Supprimer'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Bulk Delete Confirmation Dialog */}
      {bulkDeleteConfirm.isOpen && (
        <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 ${bulkDeleting ? 'cursor-wait' : ''}`}>
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center space-x-2 mb-4">
              <Trash className="w-5 h-5 text-red-600" />
              <h3 className="text-lg font-semibold">Confirmer la suppression</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Êtes-vous sûr de vouloir supprimer <strong>{bulkDeleteConfirm.practitionerIds.length}</strong> praticien
              {bulkDeleteConfirm.practitionerIds.length !== 1 ? 's' : ''} ? Cette action ne peut pas être annulée.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelBulkDelete}
                disabled={bulkDeleting}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={confirmBulkDelete}
                disabled={bulkDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {bulkDeleting ? 'Suppression...' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Hard Delete: two-step confirmation */}
      <ConfirmDefinitiveDeleteModal
        isOpen={bulkHardDeleteConfirm.isOpen}
        onConfirm={confirmBulkHardDelete}
        onClose={cancelBulkHardDelete}
        entityLabel={`praticien${bulkHardDeleteConfirm.practitionerIds.length !== 1 ? 's' : ''}`}
        count={bulkHardDeleteConfirm.practitionerIds.length}
        isLoading={bulkHardDeleting}
      />

      <ToastComponent />
    </div>
  );
}